/**
 * depositShapes.ts — the deposit row, and the flattening that produces it.
 *
 * SPLIT OUT OF `deposits.ts` SO IT CAN BE TESTED. vitest runs node-env over `.ts` files with no
 * DOM and no browser storage, so a module that imports the supabase client cannot be imported by
 * a test at all — and the array-unwrap below is exactly the rule that must be pinned by one: it
 * is what stands between the setup page and a nameless depositor on a document that prints a
 * name. `deposits.ts` holds the query; everything here is pure.
 */

import type { DepositCurrency, DepositSubject } from '@/components/contracts/deposits/types';
import { driverProfileOf } from '@/lib/driverProfile';

/** How PostgREST hands an embed back: as the row, as a list of rows, or not at all. */
export type Embedded<T> = T | T[] | null | undefined;

/**
 * PostgREST hands a to-one embed back as an object and a to-many as an array, and which one it
 * chooses depends on whether it detected the foreign key as unique. Every embed below is
 * one-per-row by the schema's own constraints, so both readings collapse to the same record —
 * but only if something unwraps them, and this is that something.
 */
export function firstOf<T>(value: Embedded<T>): T | null {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
}

/** The legacy `contract_deposit_agreements` row — the half `sign_contract` and the board read. */
export interface DepositAgreementLink {
    id: number;
    signed_at: string | null;
    agreement_url: string | null;
    origin: string | null;
}

/**
 * One linked deposit, in the superset shape.
 *
 * It satisfies `DepositSubject` field-for-field, which is what lets `DepositAgreementBlock` be
 * mounted from the setup page and from the deposits page without a second component or a second
 * prop shape.
 */
export interface ContractDeposit extends DepositSubject {
    transaction_id: number;
    remarks: string | null;
    description: string | null;
    /** Sum of this deposit's refund DEBITs. 0 when it was never refunded (which is all of them). */
    refunded: number;
    refund_count: number;
    /** The legacy agreement row, when one exists. */
    agreement: DepositAgreementLink | null;
}

/** The rows as they come off the wire, before the embeds are unwrapped. */
interface RawTransaction {
    id: number;
    amount: number | null;
    currency: string | null;
    transaction_at: string | null;
    description: string | null;
}

interface RawProfile {
    /** NULL on the driver's own row. See `driverProfileOf` — the array can hold an admin's too. */
    admin_id?: number | null;
    id?: number | null;
    first_name: string | null;
    middle_name: string | null;
    last_name: string | null;
    phone: string | null;
}

export interface RawContractDepositRow {
    id: number;
    created_at: string;
    contract_id: number | null;
    transaction_id: number;
    remarks: string | null;
    transactions: Embedded<RawTransaction>;
    contract_deposit_refunds: Array<{ id: number; transactions: Embedded<{ amount: number | null }> }> | null;
    contract_deposit_agreements: Embedded<DepositAgreementLink>;
    contracts: Embedded<{ id: number; drivers: Embedded<{ id: number; profiles: Embedded<RawProfile> }> }>;
}

/**
 * The wire rows, flattened and ordered.
 *
 * OLDEST MONEY FIRST, which is the order the deposit area stacks its cards in, with the id as the
 * tie-break so two deposits paid on the same day never reshuffle between renders.
 */
export function toContractDeposits(rows: RawContractDepositRow[]): ContractDeposit[] {
    return rows
        .map((row) => {
            const transaction = firstOf(row.transactions);
            // `driverProfileOf` AND NOT `firstOf` FOR THE LAST HOP. The name and phone below are
            // what `DepositAgreementBlock` shows and what the deposit agreement prints, and the
            // `profiles` array can carry an admin's test row against the same driver id — the one
            // embed on this row that is not one-per-row.
            const profile = driverProfileOf(firstOf(firstOf(row.contracts)?.drivers)?.profiles);
            const refunds = row.contract_deposit_refunds ?? [];

            return {
                id: row.id,
                contract_id: row.contract_id ?? null,
                transaction_id: row.transaction_id,
                remarks: row.remarks ?? null,
                created_at: row.created_at,
                amount: transaction?.amount ?? 0,
                currency: (transaction?.currency ?? 'GHS') as DepositCurrency,
                transaction_at: transaction?.transaction_at ?? row.created_at,
                description: transaction?.description ?? null,
                refunded: refunds.reduce((sum, refund) => sum + (firstOf(refund.transactions)?.amount ?? 0), 0),
                refund_count: refunds.length,
                driver_first_name: profile?.first_name ?? null,
                driver_middle_name: profile?.middle_name ?? null,
                driver_last_name: profile?.last_name ?? null,
                driver_phone: profile?.phone ?? null,
                agreement: firstOf(row.contract_deposit_agreements),
            } satisfies ContractDeposit;
        })
        .sort(
            (a, b) =>
                new Date(a.transaction_at).getTime() - new Date(b.transaction_at).getTime() || a.id - b.id,
        );
}

/**
 * The deposits half of `buildBoardSlots`' input.
 *
 * The board wants one thing per deposit — the money, when it was paid, and the legacy agreement
 * row that a document hangs off — and takes it from the same rows the deposit area draws, so the
 * two can never show a different number of deposit agreements.
 */
export function depositSubjectsFor(deposits: ContractDeposit[] | undefined) {
    return (deposits ?? []).map((deposit) => ({
        id: deposit.id,
        amount: deposit.amount,
        dated: deposit.transaction_at,
        agreement: deposit.agreement,
    }));
}

/** Gross in, minus what went back out. Both halves are shown; neither is netted silently. */
export function depositTotals(deposits: ContractDeposit[] | undefined) {
    const rows = deposits ?? [];
    return {
        count: rows.length,
        total: rows.reduce((sum, deposit) => sum + (deposit.amount || 0), 0),
        refunded: rows.reduce((sum, deposit) => sum + (deposit.refunded || 0), 0),
    };
}
